import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { debounce, isEmpty } from 'lodash';

import AppLoading from 'components/App/Loading';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import FananceDataTableInvoice from 'pages/Location/LocationDetail/Finance/FananceDataTableInvoice/index';
import ModalAddBankInfo from 'components/Modals/ModalAddBankInfo';
import Pagination from 'components/Pagination';
import Selection from 'components/App/Selection';

import cn from 'utils/classnames';
import totalPage from 'utils/totalPage';
import { getParamsFormObject } from 'utils';
import INITIAL_VALUE from 'constants/initialValue';
import { bankInfoValidation } from 'utils/validate';
import FinancePropTypes from 'propTypeModels/finance';
import {
  FINANCE_PAGE_LIMIT,
  FINANCE_BREADCRUMB,
  FINANCE_YEAR_OPTIONS,
  // FINANCE_MONTH_OPTIONS,
} from 'constants/finance';
import { storageKey, LOGIN_TYPE } from 'constants/index';

import styles from './Finance.module.scss';

function Finance({
  isLoadingGetBankingList,
  isLoadingUpdateBankingOfPlace,

  bankingList,
  placeDetail,
  me,

  actionGetPlaceDetail,
  actionGetBankingList,
  actionAddBankingOfPlace,
  actionUpdateBankingOfPlace,
  actionRequestUpdateBankingOfPlace,

  finances,
  financesLoading,
  // financeOverviewLoading,

  getFinances,
}) {
  const history = useHistory();
  const { search } = useLocation();
  const {
    value: isOpenModalAddUBackInfo,
    setTrue: onOpenModalAddUBackInfo,
    toggle: onToggleModalAddUBackInfo,
  } = useBoolean(false);
  const [isEdit, setIsEdit] = React.useState(false);

  const isIntranet = React.useMemo(() => {
    const loginType = localStorage.getItem(storageKey.LOGIN_TYPE);
    return loginType === LOGIN_TYPE.INTRANET;
  }, []);

  const isOwnerLogged = React.useMemo(() => {
    if (isIntranet) return false;
    const ownerEmail = placeDetail?.owner?.email?.trim()?.toLowerCase();
    const myEmail = me?.email?.trim()?.toLowerCase();
    return !!ownerEmail && ownerEmail === myEmail;
  }, [isIntranet, me?.email, placeDetail?.owner?.email]);

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    const nowDate = new Date();

    return {
      status: params.get('status'),
      place: params.get('place'),
      month: params.get('month') || nowDate.getMonth() + 1,
      year: params.get('year') || nowDate.getFullYear(),
      page: +(params.get('page') || 1),
    };
  }, [search]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      bankId: '',
      accountNumber: '',
      accountName: '',
    },

    validationSchema: Yup.object(bankInfoValidation),

    onSubmit: (values) => {
      if (!isIntranet) {
        actionRequestUpdateBankingOfPlace({
          data: {
            place: searchQuery.place,
            bankId: values.bankId,
            accountNumber: values.accountNumber,
            accountName: values.accountName,
          },
          callback: onToggleModalAddUBackInfo,
        });
      } else if (isEdit) {
        actionUpdateBankingOfPlace({
          placeId: searchQuery.place,
          bankAccount: placeDetail.bankAccounts[0]._id,
          data: values,
          callback: onToggleModalAddUBackInfo,
        });
      } else {
        actionAddBankingOfPlace({
          placeId: searchQuery.place,
          data: values,
          callback: onToggleModalAddUBackInfo,
        });
      }
    },
  });

  const FINANCE_MONTH_OPTIONS = React.useMemo(() => {
    let months = 12;
    const nowDate = new Date();

    if (nowDate.getFullYear() === +searchQuery.year) {
      months = nowDate.getMonth() + 1;
    }

    return Array.from({ length: months }, (item, index) => ({
      label: `Tháng ${index + 1}`,
      value: index + 1,
    }));
  }, [searchQuery.year]);

  const onChangeMonthYearSelection = React.useCallback(
    (fieldName) => (e) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          [fieldName]: e.value,
          page: 1,
        }),
      });
    },
    [history, searchQuery],
  );

  const onChangePage = React.useCallback(
    ({ selected }) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          page: selected + 1,
        }),
      });
    },
    [history, searchQuery],
  );

  const onEditBankingOfPlace = React.useCallback(() => {
    setIsEdit(true);
    onOpenModalAddUBackInfo();
  }, [onOpenModalAddUBackInfo]);

  const onDebounceSearchBank = React.useMemo(
    () =>
      debounce((e, callback) => {
        actionGetBankingList({
          q: e,
          callback,
        });
      }, 500),
    [actionGetBankingList],
  );

  React.useEffect(() => {
    actionGetBankingList({ q: '' });
  }, [actionGetBankingList]);

  React.useEffect(() => {
    if (searchQuery.place) {
      actionGetPlaceDetail({
        placeId: searchQuery.place,
      });
    }
  }, [actionGetPlaceDetail, searchQuery.place]);

  React.useEffect(() => {
    if (placeDetail.bankAccounts?.length > 0) {
      validation.setValues((prevState) => ({
        ...prevState,
        bankId: placeDetail.bankAccounts[0].bankId,
        accountName: placeDetail.bankAccounts[0].accountName,
        accountNumber: placeDetail.bankAccounts[0].accountNumber,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeDetail]);

  React.useEffect(() => {
    getFinances({
      place: searchQuery.place,
      month: searchQuery.month,
      year: searchQuery.year,
      page: searchQuery.page,
      limit: FINANCE_PAGE_LIMIT,
    });
  }, [
    searchQuery.page,
    searchQuery.year,
    searchQuery.month,
    searchQuery.place,
    getFinances,
  ]);

  if (isEmpty(placeDetail)) {
    return null;
  }

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb
        pageTitle="Tài chính"
        breadcrumbList={FINANCE_BREADCRUMB}
        className={styles.BreadcrumbContainer}
      />

      <Card className={`mb-4 ${styles.Card}`}>
        <div className={styles.BankInfo}>
          <h2 className={styles.BankTitle}>Tài khoản nhận thanh toán</h2>

          {!isEmpty(placeDetail.bankAccounts) ? (
            <>
              <div className={styles.BankDetails}>
                <div className={styles.BankCol}>
                  <span className={styles.Label}>Tên ngân hàng</span>
                  <h3 className={styles.Value}>
                    {placeDetail.bankAccounts[0].bankName}
                  </h3>
                </div>
                <div className={styles.BankCol}>
                  <span className={styles.Label}>Số tài khoản</span>
                  <h3 className={styles.Value}>
                    {`**** ${placeDetail.bankAccounts[0].accountNumber.slice(-4)}`}
                  </h3>
                </div>
                <div className={styles.BankCol}>
                  <span className={styles.Label}>Chủ thẻ</span>
                  <h3 className={styles.Value}>
                    {placeDetail.bankAccounts[0].accountName}
                  </h3>
                </div>
              </div>
              {isIntranet ? (
                <div className={styles.BankActions}>
                  <Button type="button" onClick={onEditBankingOfPlace}>
                    Chỉnh sửa
                  </Button>
                </div>
              ) : (
                !isOwnerLogged && (
                  <div className={styles.BankActions}>
                    <Button type="button" onClick={onEditBankingOfPlace}>
                      Gửi yêu cầu chỉnh sửa
                    </Button>
                  </div>
                )
              )}
            </>
          ) : (
            isIntranet && (
              <div className={styles.BankActions}>
                <Button type="button" onClick={onOpenModalAddUBackInfo}>
                  Thêm tài khoản
                </Button>
              </div>
            )
          )}
        </div>
      </Card>

      <Card className="mb-4">
        <div className={styles.HeaderContainer}>
          <div className={styles.HeaderTop}>
            <div>
              <h2 className={styles.HeaderTitle}>Hóa đơn</h2>
              <p className={styles.HeaderDescription}>
                Bookese sẽ gửi thanh toán vào
                {' '}
                <span className={styles.HeaderDescriptionBold}>thứ 4</span>
                {' '}
                mỗi tuần
              </p>
            </div>

            <div className={styles.FilterWrapper}>
              <span className={styles.HeaderDescription}>Lọc theo</span>

              <Selection
                value={+searchQuery.month}
                options={FINANCE_MONTH_OPTIONS}
                selectedClassName={styles.Selection}
                onChange={onChangeMonthYearSelection('month')}
              />

              <Selection
                value={+searchQuery.year}
                options={FINANCE_YEAR_OPTIONS}
                selectedClassName={styles.Selection}
                onChange={onChangeMonthYearSelection('year')}
              />
            </div>
          </div>
        </div>

        {financesLoading ? (
          <AppLoading loading={financesLoading} loaderOnPage />
        ) : (
          <FananceDataTableInvoice
            finances={finances?.data ?? []}
            placeId={searchQuery.place}
          />
        )}
      </Card>

      <Pagination
        page={searchQuery.page}
        pageCount={totalPage(finances?.total ?? 0, FINANCE_PAGE_LIMIT)}
        onChangePage={onChangePage}
      />

      <ModalAddBankInfo
        isEdit={isEdit}
        isOpen={isOpenModalAddUBackInfo}
        loadingSearchBanking={isLoadingGetBankingList}
        loadingUpdateBankingOfPlace={isLoadingUpdateBankingOfPlace}
        bankingList={bankingList}
        validation={validation}
        onSearchBank={onDebounceSearchBank}
        onToggleModal={onToggleModalAddUBackInfo}
        isIntranet={isIntranet}
      />
    </div>
  );
}

Finance.propTypes = {
  isLoadingGetBankingList: PropTypes.bool.isRequired,
  isLoadingUpdateBankingOfPlace: PropTypes.bool.isRequired,

  bankingList: PropTypes.instanceOf(Array),
  placeDetail: PropTypes.instanceOf(Object),

  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionGetBankingList: PropTypes.func.isRequired,
  actionAddBankingOfPlace: PropTypes.func.isRequired,
  actionUpdateBankingOfPlace: PropTypes.func.isRequired,
  actionRequestUpdateBankingOfPlace: PropTypes.func.isRequired,

  finances: FinancePropTypes.invoicePagination,
  financesLoading: PropTypes.bool.isRequired,
  // financeOverviewLoading: PropTypes.bool.isRequired,

  getFinances: PropTypes.func.isRequired,
  me: PropTypes.instanceOf(Object),
};

Finance.defaultProps = {
  finances: null,
  bankingList: INITIAL_VALUE.ARRAY,
  placeDetail: INITIAL_VALUE.OBJECT,
  me: INITIAL_VALUE.OBJECT,
};

export default Finance;
